import { Link } from "@mui/material";
import {
	ProctoringAssessmentConfigurationV2,
	ProctoringMethod,
	ProctoringOption,
} from "@verity/proctoring-option/types";
import { ReactNode } from "react";
import { Trans } from "react-i18next";
import i18nPackage from "../../../i18n/i18n";
import BlockApplications from "../../images/configuration/block-applications.svg?react";
import BlockVirtualMachines from "../../images/configuration/block-virtual-machine.svg?react";
import Calculator from "../../images/configuration/calculator.svg?react";
import CloseOpenTabs from "../../images/configuration/close-open-tabs.svg?react";
import CodeEditor from "../../images/configuration/code-editor.svg?react";
import AudioTranscript from "../../images/configuration/default-list.svg?react";
import DisableClipboard from "../../images/configuration/disable-clipboard.svg?react";
import DisableFileDownload from "../../images/configuration/disable-download.svg?react";
import DisableExtensions from "../../images/configuration/disable-extension.svg?react";
import DisableNewTab from "../../images/configuration/disable-new-tab.svg?react";
import DisablePrinting from "../../images/configuration/disable-printing.svg?react";
import DisableRightClick from "../../images/configuration/disable-right-click.svg?react";
import DisableSpellcheck from "../../images/configuration/disable-spellcheck.svg?react";
import DisableVirtualCamera from "../../images/configuration/disable-virtual-camera.svg?react";
import FaceDetection from "../../images/configuration/face-detection.svg?react";
import EnableGeofence from "../../images/configuration/geofence.svg?react";
import GuidedAccess from "../../images/configuration/guided-access.svg?react";
import IdVerification from "../../images/configuration/id-verification.svg?react";
import IpAddressIcon from "../../images/configuration/ip-address.svg?react";
import FlaggedKeywords from "../../images/configuration/keyword-list.svg?react";
import LogInstalledExtensions from "../../images/configuration/log-installed-extensions.svg?react";
import ManualEndRecording from "../../images/configuration/manual-end-proctoring.svg?react";
import ObjectDetection from "../../images/configuration/object-detection.svg?react";
import OnlyOneScreen from "../../images/configuration/one-screen.svg?react";
import RecordAppsActivity from "../../images/configuration/record-application.svg?react";
import RecordAudio from "../../images/configuration/record-audio.svg?react";
import RecordClipboard from "../../images/configuration/record-clipboard.svg?react";
import RecordKeyboard from "../../images/configuration/record-keyboard.svg?react";
import RecordScreen from "../../images/configuration/record-screen.svg?react";
import RecordVideo from "../../images/configuration/record-video.svg?react";
import RecordWebTraffic from "../../images/configuration/record-web-traffic.svg?react";
import ReferenceMaterial from "../../images/configuration/reference-material.svg?react";
import RestrictHandoff from "../../images/configuration/restrict-handoff.svg?react";
import RestrictDevices from "../../images/configuration/restrict-mobile.svg?react";
import ScanRoom from "../../images/configuration/scan-room.svg?react";
import detectSidePanel from "../../images/configuration/side-panel.svg?react";
import Signature from "../../images/configuration/signature.svg?react";
import TabControl from "../../images/configuration/tab-control.svg?react";
import ViewRunningApplications from "../../images/configuration/view-running-applications.svg?react";
import WebsiteAccess from "../../images/configuration/website-access.svg?react";
import Whiteboard from "../../images/configuration/whiteboard.svg?react";
import ScratchNotes from "../../images/configuration/work-attachments.svg?react";

// DisableFeatures + OtherActivies got combined, but didn't move all the translations into otherActivities in case the categories get changed again.
export const PROCTORING_OPTION_CATEGORIES = ["record", "verification", "otherActivities", "showTools"] as const;

export type ProctoringOptionCategory = (typeof PROCTORING_OPTION_CATEGORIES)[number];

// Accommodations is combined with confirmation
export type ConfigurationPageName =
	"thirdParty" | "platform" | "profile" | "configuration" | "method" | "liveSessions" | "confirmation";

export type StandaloneConfigurationPageName = "assessmentDetails" | "inviteTestTakers" | "editQuiz" | "review";

export type VerityAssessmentPageName =
	"assessmentType" | "assessmentDetails" | "instructions" | "questions" | "editQuiz" | "review";

export type ThirdPartyPageName = "thirdParty" | "editQuiz" | "review";

export type EditQuizStep =
	| ConfigurationPageName
	| StandaloneConfigurationPageName
	| ProctoringOptionCategory
	| VerityAssessmentPageName
	| ThirdPartyPageName;

export type DisplayPlatform = "extension" | "lockdown" | "mobile" | "chromeOs" | "browser";

const restrictDevicesDesALabel = i18nPackage.t(
	"editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.descriptionAriaLabel",
);

export type ProctoringOptionTextConfig = {
	header: string;
	description: ReactNode;
	icon?: typeof RecordVideo;
	category?: ProctoringOptionCategory;
	settingDescription?: ReactNode;
	compactHeader?: string;
};

export const PROCTORING_OPTION_TEXT: {
	[O in keyof ProctoringOption]-?: ProctoringOptionTextConfig;
} = {
	videoRecordingMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.videoRecordingMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.videoRecordingMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.videoRecordingMode.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.videoRecordingMode.compactHeader"),
		icon: RecordVideo,
		category: "record",
	},
	audioRecordingMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.compactHeader"),
		icon: RecordAudio,
		category: "record",
	},
	recordScreen: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.recordScreen.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.recordScreen.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.recordScreen.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.recordScreen.compactHeader"),
		icon: RecordScreen,
		category: "record",
	},
	scanRoom: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.scanRoom.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.scanRoom.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.scanRoom.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.scanRoom.compactHeader"),
		icon: ScanRoom,
		category: "record",
	},
	recordWebTraffic: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordWebTraffic.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordWebTraffic.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordWebTraffic.setting"),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.recordWebTraffic.compactHeader",
		),
		icon: RecordWebTraffic,
		category: "otherActivities",
	},
	detectSidePanel: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.detectSidePanel.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.detectSidePanel.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.detectSidePanel.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.detectSidePanel.compactHeader"),
		icon: detectSidePanel,
		category: "otherActivities",
	},
	recordInput: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordInput.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordInput.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordInput.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordInput.compactHeader"),
		icon: RecordKeyboard,
		category: "otherActivities",
	},
	recordClipboard: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordClipboard.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordClipboard.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordClipboard.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordClipboard.compactHeader"),
		icon: RecordClipboard,
		category: "otherActivities",
	},
	excludedWebTrafficRecordingUrls: {
		header: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.excludedWebTrafficRecordingUrls.header",
		),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.excludedWebTrafficRecordingUrls.description",
		),
		icon: RecordWebTraffic,
		category: "otherActivities",
	},

	disableNewTab: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.disableNewTab.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.disableNewTab.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.disableNewTab.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.disableNewTab.compactHeader"),
		icon: DisableNewTab,
		category: "otherActivities",
	},
	closeOpenTabs: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.closeOpenTabs.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.closeOpenTabs.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.closeOpenTabs.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.closeOpenTabs.compactHeader"),
		icon: CloseOpenTabs,
		category: "otherActivities",
	},
	disableClipboardMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableClipboardMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableClipboardMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableClipboardMode.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableClipboardMode.compactHeader",
		),
		icon: DisableClipboard,
		category: "otherActivities",
	},
	enableGuidedAccessMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.enableGuidedAccessMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.enableGuidedAccessMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.enableGuidedAccessMode.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.enableGuidedAccessMode.compactHeader",
		),
		icon: GuidedAccess,
		category: "otherActivities",
	},
	restrictHandoffMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.restrictHandoffMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.restrictHandoffMode.description"),
		settingDescription: (
			<Trans
				i18n={i18nPackage}
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.disableFeatures.restrictHandoffMode.setting"}
				components={{
					a: (
						<Link
							href="https://support.yuja.com/hc/en-us/articles/35168712896791-Disabling-Handoff-on-Apple-Devices"
							target="_blank"
							rel="noreferrer"
						/>
					),
				}}
			/>
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.restrictHandoffMode.compactHeader",
		),
		icon: RestrictHandoff,
		category: "otherActivities",
	},
	disableDownloadFiles: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableDownloadFiles.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableDownloadFiles.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableDownloadFiles.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableDownloadFiles.compactHeader",
		),
		icon: DisableFileDownload,
		category: "otherActivities",
	},
	disableSpellcheck: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableSpellcheck.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableSpellcheck.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableSpellcheck.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableSpellcheck.compactHeader",
		),
		icon: DisableSpellcheck,
		category: "otherActivities",
	},
	disablePrinting: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disablePrinting.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disablePrinting.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disablePrinting.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disablePrinting.compactHeader"),
		icon: DisablePrinting,
		category: "otherActivities",
	},
	disableRightClick: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableRightClick.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableRightClick.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableRightClick.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableRightClick.compactHeader",
		),
		icon: DisableRightClick,
		category: "otherActivities",
	},
	extensionBlockMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.extensionBlockMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.extensionBlockMode.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.extensionBlockMode.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.extensionBlockMode.compactHeader",
		),
		icon: DisableExtensions,
		category: "otherActivities",
	},
	tabWhiteList: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.tabWhiteList.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.tabWhiteList.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.tabWhiteList.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.tabWhiteList.compactHeader"),
		icon: WebsiteAccess,
		category: "otherActivities",
	},
	logInstalledExtensions: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.logInstalledExtensions.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.logInstalledExtensions.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.logInstalledExtensions.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.logInstalledExtensions.compactHeader",
		),
		icon: LogInstalledExtensions,
		category: "otherActivities",
	},
	tabBlockList: {
		header: "",
		description: "",
	},

	oneScreenEnforcementMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.oneScreenEnforcementMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.oneScreenEnforcementMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.oneScreenEnforcementMode.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.oneScreenEnforcementMode.compactHeader",
		),
		icon: OnlyOneScreen,
		category: "otherActivities",
	},
	blockVirtualMachines: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.blockVirtualMachines.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.blockVirtualMachines.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.blockVirtualMachines.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.blockVirtualMachines.compactHeader",
		),
		icon: BlockVirtualMachines,
		category: "otherActivities",
	},
	applicationBlockMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.applicationBlockMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.applicationBlockMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.applicationBlockMode.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.applicationBlockMode.compactHeader",
		),
		icon: BlockApplications,
		category: "otherActivities",
	},
	recordApplicationsActivity: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.recordApplicationsActivity.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.recordApplicationsActivity.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.recordApplicationsActivity.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.recordApplicationsActivity.compactHeader",
		),
		icon: RecordAppsActivity,
		category: "otherActivities",
	},
	logRunningApplications: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.logRunningApplications.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.logRunningApplications.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.logRunningApplications.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.logRunningApplications.compactHeader",
		),
		icon: ViewRunningApplications,
		category: "otherActivities",
	},
	hideOtherWindows: {
		header: "",
		description: "",
	},

	idVerification: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerification.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerification.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerification.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerification.compactHeader"),
		icon: IdVerification,
		category: "verification",
	},

	uploadSignature: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.uploadSignature.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.uploadSignature.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.uploadSignature.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.uploadSignature.compactHeader"),
		icon: Signature,
		category: "verification",
	},

	enableCalculator: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCalculator.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCalculator.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCalculator.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCalculator.compactHeader"),
		icon: Calculator,
		category: "showTools",
	},
	enableWhiteboard: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableWhiteboard.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableWhiteboard.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableWhiteboard.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableWhiteboard.compactHeader"),
		icon: Whiteboard,
		category: "showTools",
	},
	enableCodeEditor: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCodeEditor.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCodeEditor.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCodeEditor.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCodeEditor.compactHeader"),
		icon: CodeEditor,
		category: "showTools",
	},
	codeEditorRoomId: {
		header: "",
		description: "",
	},

	faceTrackingMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.faceTrackingMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.faceTrackingMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.faceTrackingMode.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.faceTrackingMode.compactHeader"),
		icon: FaceDetection,
		category: "record",
	},
	objectDetectionMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.objectDetectionMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.objectDetectionMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.objectDetectionMode.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.objectDetectionMode.compactHeader"),
		icon: ObjectDetection,
		category: "record",
	},
	secondaryVideoRecordingMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.secondaryVideoRecordingMode.header"),
		description: "",
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.record.secondaryVideoRecordingMode.compactHeader",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.record.secondaryVideoRecordingMode.setting",
		),
		category: "record",
	},

	isExtension: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.extension"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.extensionDescription"),
	},
	isLockdown: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.lockdown"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.lockdownDescription"),
	},
	isMobile: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.mobile"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.mobileDescription"),
	},
	isChromeOs: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.chromeOs"),
		description: "",
	},
	isBrowser: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.browser"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.browserDescription"),
	},

	keyboardShortcutBlockList: {
		header: "",
		description: "",
	},
	blockWindowedApplications: {
		header: "",
		description: "",
	},
	applicationBlockList: {
		header: "",
		description: "",
	},
	allowedApplicationsList: {
		header: "",
		description: "",
	},
	allowedExtensionsList: {
		header: "",
		description: "",
	},
	showRecordingPanel: {
		header: "",
		description: "",
	},
	enforceLockdown: {
		header: "",
		description: "",
	},
	enforceMobile: {
		header: "",
		description: "",
	},
	idVerificationEnableAi: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerificationEnableAi.header"),
		description: "",
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.verification.idVerificationEnableAi.compactHeader",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.verification.idVerificationEnableAi.setting",
		),
		category: "verification",
	},
	quietEnvironment: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.quietEnvironment.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.quietEnvironment.description"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.quietEnvironment.compactHeader"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.quietEnvironment.setting"),
		category: "record",
	},
	uploadScratchNotes: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.uploadScratchNotes.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.uploadScratchNotes.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.uploadScratchNotes.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.uploadScratchNotes.compactHeader",
		),
		icon: ScratchNotes,
		category: "otherActivities",
	},
	idVerificationMode: {
		header: "",
		description: "",
	},
	restrictDevices: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.description"),
		settingDescription: (
			<Trans
				i18n={i18nPackage}
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.setting"}
				components={{
					a: (
						<Link
							href="https://support.yuja.com/hc/en-us/articles/18378524033559-Overview-of-YuJa-Verity-Proctor-Configurations"
							target="_blank"
							rel="noreferrer"
							aria-label={restrictDevicesDesALabel}
						/>
					),
				}}
			/>
		),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.compactHeader"),
		icon: RestrictDevices,
		category: "otherActivities",
	},
	enableManualEndProctoring: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableManualEndProctoring.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.enableManualEndProctoring.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.enableManualEndProctoring.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.enableManualEndProctoring.compactHeader",
		),
		icon: ManualEndRecording,
		category: "otherActivities",
	},
	enableReferenceMaterial: {
		header: "",
		description: "",
	},
	referenceMaterialMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.referenceMaterialMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.referenceMaterialMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.referenceMaterialMode.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.referenceMaterialMode.compactHeader"),
		icon: ReferenceMaterial,
		category: "showTools",
	},
	audioTranscription: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audio.audioTranscription.header"),
		description: (
			<Trans
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.record.audio.audioTranscription.description"}
				i18n={i18nPackage}
				components={{ b: <strong /> }}
			/>
		),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audio.audioTranscription.setting"),
		icon: AudioTranscript,
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audio.audioTranscription.compactHeader"),
		category: "record",
	},
	flaggedKeywords: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audio.flaggedKeywords.header"),
		description: (
			<Trans
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.record.audio.flaggedKeywords.description"}
				i18n={i18nPackage}
				components={{ b: <strong /> }}
			/>
		),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audio.flaggedKeywords.setting"),
		icon: FlaggedKeywords,
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audio.flaggedKeywords.compactHeader"),
		category: "record",
	},
	enableGeofence: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableGeofence.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableGeofence.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableGeofence.setting"),
		compactHeader: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableGeofence.compactHeader"),
		icon: EnableGeofence,
		category: "otherActivities",
	},
	geofenceLocations: {
		header: "",
		description: "",
	},
	enableIpAddressRestriction: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableIpAddressRestriction.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.enableIpAddressRestriction.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.enableIpAddressRestriction.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.enableIpAddressRestriction.compactHeader",
		),
		category: "otherActivities",
		icon: IpAddressIcon,
	},
	allowedIpAddresses: {
		header: "",
		description: "",
	},
	disableVirtualCamera: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableVirtualCamera.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableVirtualCamera.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableVirtualCamera.setting",
		),
		compactHeader: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableVirtualCamera.compactHeader",
		),
		icon: DisableVirtualCamera,
		category: "otherActivities",
	},
};

// For UI that groups ProctoringOptionKey and doesn't have a dedicated proctoringOption key
const GROUPED_OPTION_KEYS = ["tabControl"] as const;

export type GroupedOptionKey = (typeof GROUPED_OPTION_KEYS)[number];

export type RenderableProctoringKey = keyof ProctoringOption | GroupedOptionKey;

const TAB_KEYS_LIST = [
	"closeOpenTabs",
	"disableNewTab",
	"recordWebTraffic",
	"excludedWebTrafficRecordingUrls",
	"tabWhiteList",
] as const;

// The UI wants these tab keys to be grouped under "Tab Control". Each of these keys has it's own UI under "Tab Control".
// UI also wants TabControl to have it's own header/description/icon, but there is no database key to store "header/description/icon/etc"
// Also the UI for ConfigurationSummaryList returns a map with ProctoringOption as the key, but don't want to create a database field just for "tabControl"
type TabProctoringKey = (typeof TAB_KEYS_LIST)[number];

export function isTabKey(key: RenderableProctoringKey): key is TabProctoringKey {
	return (TAB_KEYS_LIST as readonly string[]).includes(key);
}

export function isGroupedOptionKey(key: RenderableProctoringKey): key is GroupedOptionKey {
	return (GROUPED_OPTION_KEYS as readonly string[]).includes(key);
}

export const GROUPED_OPTION_TEXT: Record<GroupedOptionKey, ProctoringOptionTextConfig> = {
	tabControl: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.groupedOption.tabControl.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.groupedOption.tabControl.description"),
		icon: TabControl,
		category: "otherActivities",
	},
};

export const CANDIDATE_ONLY_OPTIONS: RenderableProctoringKey[] = ["enableCodeEditor"];

export function getOptionsInCategory(proctoringOptionCategory: ProctoringOptionCategory) {
	return Object.entries(PROCTORING_OPTION_TEXT)
		.filter((x) => x[1].category === proctoringOptionCategory && x[1].settingDescription)
		.map((x) => x[0] as keyof ProctoringOption);
}

export function getDisplayedPlatformsFromProctoringOptions(
	options: Partial<Pick<ProctoringOption, "isExtension" | "isLockdown" | "isMobile" | "isChromeOs" | "isBrowser">>,
): DisplayPlatform[] {
	const platforms: DisplayPlatform[] = [];
	if (options.isExtension) {
		platforms.push("extension");
	}
	if (options.isLockdown) {
		platforms.push("lockdown");
	}
	if (options.isMobile) {
		platforms.push("mobile");
	}
	if (options.isLockdown && options.isChromeOs) {
		platforms.push("chromeOs");
	}
	if (options.isBrowser) {
		platforms.push("browser");
	}
	return platforms;
}

export function getProctoringPlatformName(platform: DisplayPlatform, compact = false): string {
	if (compact) {
		return i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.platform.compact.${platform}`);
	}
	return i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.platform.${platform}`);
}

export function getPlatformTextFromProctoringOptions(
	options: Partial<Pick<ProctoringOption, "isExtension" | "isLockdown" | "isMobile" | "isChromeOs" | "isBrowser">>,
	compact = false,
) {
	const platformText = getDisplayedPlatformsFromProctoringOptions(options).map((platform) =>
		getProctoringPlatformName(platform, compact),
	);
	if (platformText.length === 0) {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.none");
	} else if (platformText.length === 1) {
		return platformText[0];
	} else if (platformText.length === 2) {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.and2", {
			platform1: platformText[0],
			platform2: platformText[1],
		});
	} else {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.andMany", {
			platforms: platformText.slice(0, -1).join(", "),
			lastPlatform: platformText[platformText.length - 1],
		});
	}
}

export function getProctoringMethodName(method: ProctoringMethod): string {
	if (method === "live") {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.isLiveProctored");
	} else if (method === "pop-in") {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.isLivePopIn");
	} else if (method === "pop-in-post-review") {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.enablePostReview");
	}
	return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.automated");
}

export function getProctoringMethodNameFromConfiguration(
	proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
) {
	if (proctoringAssessmentConfiguration.isLiveProctored) {
		return getProctoringMethodName("live");
	} else if (proctoringAssessmentConfiguration.isLivePopIn) {
		if (proctoringAssessmentConfiguration.enablePostReview) {
			return getProctoringMethodName("pop-in-post-review");
		} else {
			return getProctoringMethodName("pop-in");
		}
	}
	return getProctoringMethodName("automated");
}

export function isProctoringOptionCategory(pageName: string): pageName is ProctoringOptionCategory {
	return PROCTORING_OPTION_CATEGORIES.includes(pageName as ProctoringOptionCategory);
}

export function getEditQuizStepName(pageName: EditQuizStep): string {
	return i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.stepName.${pageName}`);
}
